// Horario de venta de alcohol (off-premise, Texas por defecto). Funciones puras,
// sin dependencias de servidor: se importa desde el cliente (types/location-features.ts).
//
// Una ventana es `{ from, until }` en HH:MM del reloj local del negocio. `until`
// puede pasar de "24:00" (hasta "30:00") para cruzar la medianoche: la ventana del
// sábado 07:00–25:00 sigue vigente hasta la 01:00 del domingo. `until` es exclusivo.

export type HHMM = string
export type Win = { from: HHMM; until: HHMM } | null
/** Índice 0 = domingo … 6 = sábado (como `Date#getDay`). */
export type AlcoholSaleHours = { windows: [Win, Win, Win, Win, Win, Win, Win] }

const MINUTES_PER_DAY = 1440
const MAX_UNTIL_MINUTES = 30 * 60

export const TEXAS_OFF_PREMISE_HOURS: AlcoholSaleHours = {
  windows: [
    { from: "10:00", until: "24:00" }, // domingo
    { from: "07:00", until: "24:00" }, // lunes
    { from: "07:00", until: "24:00" },
    { from: "07:00", until: "24:00" },
    { from: "07:00", until: "24:00" },
    { from: "07:00", until: "24:00" }, // viernes
    { from: "07:00", until: "25:00" }, // sábado (hasta la 01:00 del domingo)
  ],
}

const HHMM_RE = /^(\d{2}):(\d{2})$/

function toMinutes(v: unknown): number | null {
  if (typeof v !== "string") return null
  const m = HHMM_RE.exec(v)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (min > 59) return null
  const total = h * 60 + min
  return total > MAX_UNTIL_MINUTES ? null : total
}

/**
 * Valida un jsonb editable a mano. Solo acepta `{ windows }` con exactamente 7
 * entradas, cada una `null` o `{ from, until }` en HH:MM con `from < until`,
 * `from` dentro del día y `until <= 30:00`. Cualquier otra cosa ⇒ `null`
 * (el llamador cae al horario de Texas).
 */
export function parseAlcoholSaleHours(raw: unknown): AlcoholSaleHours | null {
  if (!raw || typeof raw !== "object") return null
  const windows = (raw as { windows?: unknown }).windows
  if (!Array.isArray(windows) || windows.length !== 7) return null
  const out: Win[] = []
  for (const w of windows) {
    if (w === null) {
      out.push(null)
      continue
    }
    if (!w || typeof w !== "object") return null
    const { from, until } = w as { from?: unknown; until?: unknown }
    const f = toMinutes(from)
    const u = toMinutes(until)
    if (f === null || u === null) return null
    if (f >= MINUTES_PER_DAY || f >= u) return null
    out.push({ from: from as string, until: until as string })
  }
  return { windows: out as AlcoholSaleHours["windows"] }
}

const WEEKDAY_INDEX: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

/**
 * Día (0 = domingo) y minuto del día en `timeZone`. `null` si no se puede saber: zona
 * horaria inválida o fecha inválida (Intl lanza RangeError) o un día que no se reconoce.
 * El llamador falla CERRADO con `null` (no se vende alcohol).
 */
function localDayAndMinute(at: Date, timeZone: string): { dow: number; minute: number } | null {
  let parts: Intl.DateTimeFormatPart[]
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(at)
  } catch {
    return null
  }
  let weekday = ""
  let hour = Number.NaN
  let minute = Number.NaN
  for (const p of parts) {
    if (p.type === "weekday") weekday = p.value
    else if (p.type === "hour") hour = Number(p.value) % 24
    else if (p.type === "minute") minute = Number(p.value)
  }
  const dow = WEEKDAY_INDEX[weekday]
  if (dow === undefined || !Number.isInteger(hour) || !Number.isInteger(minute)) return null
  return { dow, minute: hour * 60 + minute }
}

/**
 * ¿Se puede vender alcohol en `at`? Mira el reloj local de `timeZone`:
 * permitido si el minuto cae en `[from, min(until, 24:00))` de la ventana de hoy,
 * o en `[00:00, until − 24:00)` cuando la ventana de AYER pasa de las 24:00.
 * `hours = null` ⇒ horario de Texas. Falla CERRADO (`false`) si la zona horaria o la
 * fecha son inválidas o el día no se reconoce.
 */
export function alcoholSaleAllowed(at: Date, timeZone: string, hours: AlcoholSaleHours | null): boolean {
  const windows = (hours ?? TEXAS_OFF_PREMISE_HOURS).windows
  const local = localDayAndMinute(at, timeZone)
  if (!local) return false
  const { dow, minute } = local

  const today = windows[dow]
  if (today) {
    const from = toMinutes(today.from)
    const until = toMinutes(today.until)
    if (from !== null && until !== null && minute >= from && minute < Math.min(until, MINUTES_PER_DAY)) return true
  }

  const yesterday = windows[(dow + 6) % 7]
  if (yesterday) {
    const until = toMinutes(yesterday.until)
    if (until !== null && until > MINUTES_PER_DAY && minute < until - MINUTES_PER_DAY) return true
  }
  return false
}
